import React from "react";
import { RefreshCw } from "lucide-react";
import { useLanguage } from "../components/ui/LanguageProvider";

/**
 * 당겨서 새로고침 — 폰 셸 본문(<main>) 전용. 데모·근거: docs/MOBILE_WEBAPP.md §7 (Apple HIG Refresh content controls).
 *  - 터치만. 본문이 맨 위(scrollTop 0)에서 시작해 "대부분 아래로" 끌 때만 잡는다(가로 스크롤·일반 스크롤 방해 금지).
 *  - 끄는 거리의 절반만 따라온다(저항 0.5, 최대 100px). 64px 넘겨 놓으면 화면의 RefreshAction 버튼을 대신 누른다
 *    (`[data-refresh-action] button` — 헤더 트레이와 같은 함수). 버튼이 없는 화면은 그냥 되돌아간다.
 *  - 돌아가는 시간: 최소 0.8초, 버튼이 잠겨(로딩) 있으면 풀릴 때까지(최대 4초).
 *  - 매 터치 이동마다 리렌더하지 않게 표시기는 ref 로 직접 움직인다. 동작 줄이기면 전환·회전 끔.
 */
const THRESHOLD = 64;
const MAX_PULL = 100;
const RESIST = 0.5;
const HOLD_AT = 56; // 새로고침 중 표시기가 머무는 위치
const HIDDEN_Y = -44; // 표시기(40px)가 화면 밖에 숨는 위치
const MIN_SPIN = 800;
const MAX_SPIN = 4000;

const reducedMotion = () =>
  typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** 손가락 아래 요소부터 main 까지 — 안쪽 스크롤 영역이 이미 내려가 있으면 그 영역의 스크롤이 우선 */
function insideScrolledArea(target: EventTarget | null, main: HTMLElement): boolean {
  for (let n = target instanceof Element ? target : null; n && n !== main; n = n.parentElement) {
    if (n.scrollTop > 0) return true;
  }
  return false;
}

export default function PullToRefresh({ mainRef }: { mainRef: React.RefObject<HTMLElement> }) {
  const { t } = useLanguage();
  const boxRef = React.useRef<HTMLDivElement>(null);
  const turnRef = React.useRef<HTMLSpanElement>(null);
  const [refreshing, setRefreshing] = React.useState(false);

  React.useEffect(() => {
    const main = mainRef.current;
    if (!main) return undefined;
    let startX = 0;
    let startY = 0;
    let tracking = false;
    let engaged = false;
    let busy = false;
    let dist = 0;
    let poll = 0;

    const paint = (d: number, animate: boolean) => {
      const box = boxRef.current;
      if (!box) return;
      box.style.transition = animate && !reducedMotion() ? "transform 250ms var(--ease-out), opacity 250ms var(--ease-out)" : "none";
      box.style.transform = `translate(-50%, ${HIDDEN_Y + d}px)`;
      box.style.opacity = d > 0 ? String(Math.min(1, d / THRESHOLD)) : "0";
      const ready = d >= THRESHOLD || busy;
      box.classList.toggle("text-primary", ready);
      box.classList.toggle("text-ink-muted", !ready);
      if (turnRef.current) turnRef.current.style.transform = busy || reducedMotion() ? "" : `rotate(${d * 3}deg)`;
    };

    const finish = () => {
      window.clearInterval(poll);
      busy = false;
      setRefreshing(false);
      paint(0, true);
    };

    const run = (btn: HTMLButtonElement) => {
      busy = true;
      setRefreshing(true);
      paint(HOLD_AT, true);
      btn.click();
      const start = Date.now();
      poll = window.setInterval(() => {
        const elapsed = Date.now() - start;
        // 0.8초가 지났고 버튼이 다시 풀렸으면(로딩 끝) 끝. 버튼이 사라졌어도 끝. 최대 4초.
        if ((elapsed >= MIN_SPIN && (!btn.isConnected || !btn.disabled)) || elapsed >= MAX_SPIN) finish();
      }, 100);
    };

    const reset = () => {
      tracking = false;
      if (engaged) { engaged = false; dist = 0; paint(0, true); }
    };

    const onStart = (e: TouchEvent) => {
      tracking = false;
      engaged = false;
      if (busy || e.touches.length !== 1) return;
      if (main.scrollTop > 0 || insideScrolledArea(e.target, main)) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      tracking = true;
    };

    const onMove = (e: TouchEvent) => {
      if (!tracking) return;
      const dx = e.touches[0].clientX - startX;
      const dy = e.touches[0].clientY - startY;
      if (!engaged) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        // 아래로, 그리고 가로보다 확실히 세로일 때만 잡는다. 아니면 이 터치는 끝까지 놓아준다.
        if (dy > 0 && dy > Math.abs(dx) * 1.5 && main.scrollTop <= 0) {
          engaged = true;
          startY = e.touches[0].clientY; // 판정 여유(6px)만큼 튀지 않게 여기서 0 부터
        } else {
          tracking = false;
          return;
        }
      }
      const pulled = e.touches[0].clientY - startY;
      if (pulled < 0) { reset(); return; } // 다시 위로 밀면 일반 스크롤로 돌려준다
      if (e.cancelable) e.preventDefault();
      dist = Math.min(MAX_PULL, pulled * RESIST);
      paint(dist, false);
    };

    const onEnd = () => {
      tracking = false;
      if (!engaged) return;
      engaged = false;
      const passed = dist >= THRESHOLD;
      dist = 0;
      const btn = passed ? main.querySelector<HTMLButtonElement>("[data-refresh-action] button:not([disabled])") : null;
      if (btn) run(btn);
      else paint(0, true);
    };

    main.addEventListener("touchstart", onStart, { passive: true });
    main.addEventListener("touchmove", onMove, { passive: false });
    main.addEventListener("touchend", onEnd);
    main.addEventListener("touchcancel", reset);
    return () => {
      window.clearInterval(poll);
      main.removeEventListener("touchstart", onStart);
      main.removeEventListener("touchmove", onMove);
      main.removeEventListener("touchend", onEnd);
      main.removeEventListener("touchcancel", reset);
    };
  }, [mainRef]);

  return (
    <div
      ref={boxRef}
      data-pull-refresh=""
      aria-hidden={!refreshing}
      className="pointer-events-none absolute left-1/2 top-0 z-10 grid h-10 w-10 place-items-center rounded-pill bg-canvas text-ink-muted"
      style={{ transform: `translate(-50%, ${HIDDEN_Y}px)`, opacity: 0, boxShadow: "0 2px 8px rgba(31,42,68,.18)" }}
    >
      <span ref={turnRef} className="grid place-items-center">
        <RefreshCw size={18} aria-hidden className={refreshing ? "animate-spin motion-reduce:animate-none" : ""} />
      </span>
      <span role="status" className="sr-only">{refreshing ? t("새로고침 중", "Refreshing") : ""}</span>
    </div>
  );
}
